import React, { useState, useEffect } from 'react';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { CONFIG } from '../config';
import { EditIcon, TrashIcon, MessageIcon, WhatsAppIcon } from '../components/icons/Icons';

const ListGuestsPage = ({ allowedChannels = 'both', customGroups = null }) => {
  const smsEnabled = allowedChannels === 'sms' || allowedChannels === 'both';
  const waEnabled = allowedChannels === 'whatsapp' || allowedChannels === 'both';
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortCol, setSortCol] = useState('name');
  const [sortDir, setSortDir] = useState('asc');

  // Edit modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editFormData, setEditFormData] = useState({
    name: '',
    phone_number: '',
    groups: [],
    msgPreferences: { sms: false, whatsapp: false }
  });
  const [saving, setSaving] = useState(false);

  const groupOptions = customGroups || ['Bride', 'Groom', 'Bridal Party', 'Event Staff'];

  const handleSort = (col) => {
    if (sortCol === col) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortCol(col);
      setSortDir('asc');
    }
  };

  const sortedUsers = [...users].sort((a, b) => {
    let aVal, bVal;
    if (sortCol === 'name') {
      aVal = (a.name || '').toLowerCase();
      bVal = (b.name || '').toLowerCase();
    } else if (sortCol === 'phone_number') {
      aVal = a.phone_number || '';
      bVal = b.phone_number || '';
    } else if (sortCol === 'groups') {
      const clean = g => Array.isArray(g) ? g.filter(x => !['SMS', 'WhatsApp'].includes(x)).join(', ').toLowerCase() : '';
      aVal = clean(a.groups);
      bVal = clean(b.groups);
    } else if (sortCol === 'msg_pref') {
      const score = u => { const p = getMessagePreferences(u.groups); return (p.sms ? 2 : 0) + (p.whatsapp ? 1 : 0); };
      aVal = score(a);
      bVal = score(b);
    } else {
      return 0;
    }
    if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  const SortIcon = ({ col }) => (
    <span style={{ marginLeft: '4px', opacity: sortCol === col ? 1 : 0.3, fontSize: '0.75rem' }}>
      {sortCol === col ? (sortDir === 'asc' ? '▲' : '▼') : '⇅'}
    </span>
  );

  const downloadCSV = () => {
    const rows = [
      ['Name', 'Phone Number', 'Message Preference', 'Groups'],
      ...sortedUsers.map(user => {
        const prefs = getMessagePreferences(user.groups);
        const pref = prefs.sms && prefs.whatsapp ? 'Both' : prefs.sms ? 'SMS' : prefs.whatsapp ? 'WhatsApp' : '';
        const groups = Array.isArray(user.groups)
          ? user.groups.filter(g => !['SMS', 'WhatsApp'].includes(g)).join(', ')
          : '';
        return [user.name || '', user.phone_number || '', pref, groups];
      }),
    ];
    const csv = rows.map(r => r.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'guests.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  // Helper to check message preferences
  const getMessagePreferences = (groups) => {
    if (!Array.isArray(groups)) return { sms: false, whatsapp: false };
    const groupsLower = groups.map(g => g.toLowerCase());
    return {
      sms: groupsLower.includes('sms'),
      whatsapp: groupsLower.includes('whatsapp')
    };
  };

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError(null);

      const resp = await fetch(`${CONFIG.apiBaseUrl}/list_guests`, {
        headers: { Accept: 'application/json' },
        credentials: 'include',
      });

      if (!resp.ok) {
        const text = await resp.text();
        throw new Error(`${resp.status}: ${text}`);
      }

      const data = await resp.json();
      const usersList = Array.isArray(data) ? data : (data.users || []);
      setUsers(usersList);
    } catch (err) {
      console.error('Failed to load users:', err);
      setError(err.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  // Handle Edit
  const handleEdit = (user) => {
    setEditingUser(user);
    const userGroups = Array.isArray(user.groups) ? user.groups : [];
    const prefs = getMessagePreferences(userGroups);
    // Filter out SMS and WhatsApp from groups for display
    const filteredGroups = userGroups.filter(g => !['SMS', 'WhatsApp'].includes(g));
    setEditFormData({
      name: user.name || '',
      phone_number: user.phone_number || '',
      groups: filteredGroups,
      msgPreferences: {
        sms: smsEnabled && !waEnabled ? true : prefs.sms,
        whatsapp: waEnabled && !smsEnabled ? true : prefs.whatsapp,
      }
    });
    setShowEditModal(true);
  };

  const handleMsgPrefChange = (type) => {
    setEditFormData(prev => ({
      ...prev,
      msgPreferences: { ...prev.msgPreferences, [type]: !prev.msgPreferences[type] }
    }));
  };

  const handleGroupChange = (group) => {
    setEditFormData(prev => ({
      ...prev,
      groups: prev.groups.includes(group)
        ? prev.groups.filter(g => g !== group)
        : [...prev.groups, group]
    }));
  };

  const handleSaveEdit = async () => {
    // Validation
    if (!editFormData.name.trim()) {
      alert('Name is required');
      return;
    }
    if (!editFormData.phone_number.trim()) {
      alert('Phone number is required');
      return;
    }
    if (editFormData.groups.length === 0) {
      alert('Please select at least one group');
      return;
    }
    if (smsEnabled && waEnabled && !editFormData.msgPreferences.sms && !editFormData.msgPreferences.whatsapp) {
      alert('Please select at least one message preference (SMS or WhatsApp)');
      return;
    }

    try {
      setSaving(true);

      // Build groups array including message preferences
      const allGroups = [...editFormData.groups];
      const effectiveSms = smsEnabled && !waEnabled ? true : editFormData.msgPreferences.sms;
      const effectiveWa = waEnabled && !smsEnabled ? true : editFormData.msgPreferences.whatsapp;
      if (effectiveSms) allGroups.push('SMS');
      if (effectiveWa) allGroups.push('WhatsApp');

      const payload = {
        name: editFormData.name.trim(),
        phone_number: editFormData.phone_number.trim(),
        groups: allGroups
      };

      const resp = await fetch(
        `${CONFIG.apiBaseUrl}/guests/${encodeURIComponent(editingUser.phone_number)}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
          credentials: 'include',
        }
      );

      if (!resp.ok) {
        const errorData = await resp.json();
        throw new Error(errorData.error || errorData.detail || 'Failed to update guest');
      }

      setShowEditModal(false);
      setEditingUser(null);
      loadUsers(); // Refresh the list
    } catch (err) {
      console.error('Error updating guest:', err);
      alert(`Error: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  // Handle Delete
  const handleDelete = async (user) => {
    if (!window.confirm(`Are you sure you want to delete ${user.name}?`)) {
      return;
    }

    try {
      const resp = await fetch(
        `${CONFIG.apiBaseUrl}/guests/${encodeURIComponent(user.phone_number)}`,
        {
          method: 'DELETE',
          credentials: 'include',
        }
      );

      if (!resp.ok) {
        const errorData = await resp.json();
        throw new Error(errorData.error || errorData.detail || 'Failed to delete guest');
      }

      loadUsers(); // Refresh the list
    } catch (err) {
      console.error('Error deleting guest:', err);
      alert(`Error: ${err.message}`);
    }
  };

  return (
    <div className="users-page">
      <h2 className="page-title">
        Guest list
      </h2>

      {loading && (
        <div className="loading-message">Loading guests...</div>
      )}

      {error && (
        <div className="error-message">
          Error: {error}
        </div>
      )}

      {!loading && !error && (
        <div className="users-container">
          <div className="users-header">
            Loaded {users.length} guests{users.length > 0 && (
              <span
                onClick={downloadCSV}
                style={{ marginLeft: '6px', cursor: 'pointer', color: '#6b7280', fontSize: '0.85em' }}
              >
                (Download)
              </span>
            )}
          </div>
          <div className="users-table-wrapper">
            <table className="users-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th onClick={() => handleSort('name')} style={{ cursor: 'pointer', userSelect: 'none' }}>Name <SortIcon col="name" /></th>
                  <th onClick={() => handleSort('phone_number')} style={{ cursor: 'pointer', userSelect: 'none' }}>Phone Number <SortIcon col="phone_number" /></th>
                  <th onClick={() => handleSort('msg_pref')} style={{ cursor: 'pointer', userSelect: 'none' }}>Msg Pref <SortIcon col="msg_pref" /></th>
                  <th onClick={() => handleSort('groups')} style={{ cursor: 'pointer', userSelect: 'none' }}>Groups <SortIcon col="groups" /></th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedUsers.map((user, index) => {
                  const prefs = getMessagePreferences(user.groups);
                  return (
                  <tr key={user.phone_number || index}>
                    <td>{index + 1}</td>
                    <td>{user.name || ''}</td>
                    <td>{user.phone_number || ''}</td>
                    <td className="msg-pref-cell">
                      {prefs.sms && (
                        <span className="msg-pref-icon msg-pref-sms" title="SMS">
                          <MessageIcon />
                        </span>
                      )}
                      {prefs.whatsapp && (
                        <span className="msg-pref-icon msg-pref-whatsapp" title="WhatsApp">
                          <WhatsAppIcon />
                        </span>
                      )}
                      {!prefs.sms && !prefs.whatsapp && (
                        <span className="msg-pref-none">-</span>
                      )}
                    </td>
                    <td>{Array.isArray(user.groups) ? user.groups.filter(g => !['SMS', 'WhatsApp'].includes(g)).join(', ') : (user.groups || '')}</td>
                    <td className="actions-cell">
                      <button
                        onClick={() => handleEdit(user)}
                        className="btn-icon btn-icon-edit"
                        title="Edit guest"
                      >
                        <EditIcon />
                      </button>
                      <button
                        onClick={() => handleDelete(user)}
                        className="btn-icon btn-icon-delete"
                        title="Delete guest"
                      >
                        <TrashIcon />
                      </button>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && editingUser && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Edit Guest</h3>

            <div className="form-group">
              <label htmlFor="editName" className="form-label">
                Name: <span className="required">*</span>
              </label>
              <input
                type="text"
                id="editName"
                value={editFormData.name}
                onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                className="form-input"
                disabled={saving}
              />
            </div>

            <div className="form-group">
              <label htmlFor="editPhone" className="form-label">
                Phone Number: <span className="required">*</span>
              </label>
              <PhoneInput
                id="editPhone"
                defaultCountry="US"
                value={editFormData.phone_number}
                onChange={(value) => setEditFormData({ ...editFormData, phone_number: value || '' })}
                disabled={saving}
              />
              <div className="input-hint">
                Note: Changing the phone number will create a new record.
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">
                Groups: <span className="required">*</span>
              </label>
              <div className="checkbox-group">
                {groupOptions.map(group => (
                  <label key={group} className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={editFormData.groups.includes(group)}
                      onChange={() => handleGroupChange(group)}
                      className="checkbox-input"
                      disabled={saving}
                    />
                    <span>{group}</span>
                  </label>
                ))}
              </div>
            </div>

            {(smsEnabled && waEnabled) && (
              <div className="form-group">
                <label className="form-label">
                  Message Preference: <span className="required">*</span>
                </label>
                <div className="checkbox-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={editFormData.msgPreferences.sms}
                      onChange={() => handleMsgPrefChange('sms')}
                      className="checkbox-input"
                      disabled={saving}
                    />
                    <span>SMS</span>
                  </label>
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={editFormData.msgPreferences.whatsapp}
                      onChange={() => handleMsgPrefChange('whatsapp')}
                      className="checkbox-input"
                      disabled={saving}
                    />
                    <span>WhatsApp</span>
                  </label>
                </div>
              </div>
            )}

            <div className="modal-actions">
              <button
                onClick={handleSaveEdit}
                className="btn btn-primary"
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
              <button
                onClick={() => setShowEditModal(false)}
                className="btn btn-secondary"
                disabled={saving}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ListGuestsPage;
